import React, { useState, useEffect, useMemo } from "react";
import { toast, deleteRequestStatusStyle, entityTypeLabel } from "../../core/helpers";

export default function DeleteRequestsPage({ user, sb, users = [], clients = [] }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_approval"); // pending_approval | approved | rejected | all
  const [rejectForId, setRejectForId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [busy, setBusy] = useState(null); // id being reviewed

  const load = async () => {
    setLoading(true);
    try {
      const rows = await sb.listDeleteRequests();
      setRequests(Array.isArray(rows) ? rows : []);
    } catch (e) {
      toast("Failed to load delete requests", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter(r => r.status === filter);
  }, [requests, filter]);

  const countPending = requests.filter(r => r.status === "pending_approval").length;
  const isAdmin = user?.role === "admin";

  const requesterName = (r) => users.find(u => u.id === r.requested_by)?.name || "Unknown user";
  const reviewerName = (r) => r.reviewed_by ? (users.find(u => u.id === r.reviewed_by)?.name || "Admin") : "";
  const entityLabel = (r) => {
    if (r.entity_type === "clients") {
      return clients.find(c => c.id === r.entity_id)?.name || `Client ${r.entity_id.slice(0,8)}`;
    }
    if (r.entity_type === "employees") {
      return users.find(u => u.id === r.entity_id)?.name || `Employee ${r.entity_id.slice(0,8)}`;
    }
    if (r.entity_type === "tasks") {
      return `Task ${r.entity_id.slice(0,8)}`;
    }
    if (r.entity_type === "invoices") {
      return `Invoice ${r.entity_id.slice(0,8)}`;
    }
    return `${r.entity_type} ${r.entity_id?.slice(0,8)}`;
  };

  const doApprove = async (r) => {
    if (!window.confirm(`Approve delete of ${entityTypeLabel(r.entity_type)} "${entityLabel(r)}"? This action is irreversible.`)) return;
    setBusy(r.id);
    try {
      await sb.reviewDeleteRequest(r.id, { status:"approved", review_note: r.review_note || "" });
      toast("Delete request approved", "success");
      await load();
    } catch (e) {
      toast(e.message || "Failed to approve request", "error");
    } finally {
      setBusy(null);
    }
  };

  const doRejectSubmit = async (r) => {
    if (!rejectNote.trim()) { toast("Please enter a rejection reason", "error"); return; }
    setBusy(r.id);
    try {
      await sb.reviewDeleteRequest(r.id, { status:"rejected", review_note: rejectNote.trim() });
      toast("Delete request rejected — requester notified", "success");
      setRejectForId(null);
      setRejectNote("");
      await load();
    } catch (e) {
      toast(e.message || "Failed to reject request", "error");
    } finally {
      setBusy(null);
    }
  };

  // render: heading + filter tabs (Pending/Approved/Rejected/All) with count badges, then list.
  return (
    <div className="page">
      <div className="page-head" style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 16, flexWrap:"wrap", gap:12}}>
        <div>
          <h2 style={{margin:0}}>Delete Requests</h2>
          <div style={{color:"#64748b", fontSize:13, marginTop:4}}>
            {countPending > 0 ? `${countPending} pending for your approval` : "No pending requests"}
          </div>
        </div>
        <div className="list-toolbar-actions" style={{gap:6, display:"flex"}}>
          {["pending_approval","approved","rejected","all"].map(f => (
            <button key={f} onClick={()=>setFilter(f)}
              className={"btn " + (filter===f ? "btn-primary" : "btn-ghost")}
              style={{padding: "6px 14px", fontSize:13}}>
              {f === "pending_approval" ? "Pending" : f.charAt(0).toUpperCase() + f.slice(1)}
              <span style={{marginLeft:6, opacity:0.8}}>
                ({f === "all" ? requests.length : requests.filter(r=>r.status===f).length})
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <div>Loading...</div> : filtered.length === 0 ? (
        <div style={{padding:"48px 16px", textAlign:"center", color:"#94a3b8"}}>
          No {filter === "all" ? "" : filter.replace("_"," ")} delete requests found.
        </div>
      ) : (
        filtered.map(r => {
          const st = deleteRequestStatusStyle(r.status);
          return (
            <div key={r.id} className="dr-card">
              <div className="dr-card-head">
                <div className="dr-card-title">
                  {entityTypeLabel(r.entity_type)}: {entityLabel(r)}
                </div>
                {st && <span className={"dr-badge " + r.status} style={{background:st.bg, color:st.color}}>
                  {st.label}
                </span>}
              </div>
              <div className="dr-card-meta">
                Requested by <strong>{requesterName(r)}</strong> on {r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}
                {r.reviewed_at && <> · Reviewed by <strong>{reviewerName(r)}</strong> on {new Date(r.reviewed_at).toLocaleString()}</>}
                {r.reassign_to && <> · Reassign to: {users.find(u=>u.id===r.reassign_to)?.name || r.reassign_to.slice(0,8)}</>}
              </div>
              {r.reason && <div className="dr-card-reason">Reason: {r.reason}</div>}
              {r.review_note && <div className="dr-card-reason" style={{borderLeftColor: r.status==="approved"?"#16a34a":"#dc2626"}}>
                {r.status === "approved" ? "Approval note" : "Rejection reason"}: {r.review_note}
              </div>}

              {r.status === "pending_approval" && isAdmin && busy !== r.id && (
                <div className="dr-card-actions">
                  <button className="btn btn-primary" onClick={()=>doApprove(r)}>
                    ✓ Approve
                  </button>
                  <button className="btn" style={{background:"#fee2e2", color:"#991b1b", borderColor:"#fecaca"}}
                    onClick={()=>{setRejectForId(r.id); setRejectNote("");}}>
                    ✗ Reject
                  </button>
                </div>
              )}
              {r.status === "pending_approval" && isAdmin && busy === r.id && <div>Processing...</div>}
              {r.status === "pending_approval" && !isAdmin && (
                <div className="dr-card-meta">Waiting for an admin to approve or reject.</div>
              )}

              {rejectForId === r.id && (
                <div className="dr-confirm-form" style={{marginTop:12}}>
                  <label>Reason for rejection (required)</label>
                  <textarea value={rejectNote} onChange={e=>setRejectNote(e.target.value)}
                    placeholder="Explain why this request is being rejected so the requester understands..."/>
                  <div className="dr-card-actions">
                    <button className="btn btn-primary" onClick={()=>doRejectSubmit(r)} disabled={busy === r.id}>
                      Submit Rejection
                    </button>
                    <button className="btn btn-ghost" onClick={()=>{setRejectForId(null); setRejectNote("");}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
